import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, CheckCircle2 } from 'lucide-react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { TextField, FormError, SubmitButton } from '@/components/auth/FormFields';
import { forgotPassword, readApiError } from '@/lib/auth-api';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      // Show success even on error to prevent email enumeration.
      // The backend responds 204 either way; only network failures land here.
      setError(readApiError(err, t('auth.error_generic')));
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <AuthLayout
        title={t('auth.check_inbox_title')}
        subtitle={t('auth.check_inbox_subtitle', { email })}
        footer={
          <>
            {t('auth.didnt_arrive')}{' '}
            <button
              onClick={() => setSent(false)}
              className="font-semibold text-accent-700 hover:underline"
            >
              {t('auth.try_again')}
            </button>
            .
          </>
        }
      >
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-5 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-sm text-emerald-900">
            <p className="font-medium">{t('auth.check_inbox_lead')}</p>
            <p className="mt-1 text-emerald-800/80">{t('auth.check_inbox_detail')}</p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.forgot_title')}
      subtitle={t('auth.forgot_subtitle')}
      footer={
        <>
          {t('auth.remember_password')}{' '}
          <Link to="/login" className="font-semibold text-accent-700 hover:underline">
            {t('auth.login_link')}
          </Link>
        </>
      }
    >
      <form className="space-y-4" onSubmit={submit} noValidate>
        <TextField
          label={t('auth.email')}
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          leadingIcon={<Mail className="h-4 w-4" />}
        />
        <FormError message={error} />
        <SubmitButton loading={loading}>{t('auth.send_reset_link')}</SubmitButton>
      </form>
    </AuthLayout>
  );
}
